import { yupResolver } from '@hookform/resolvers/yup';
import { useQueryClient } from '@tanstack/react-query';
import {
  convertFromYesNo,
  convertToYesNoEnum,
  isDefined,
  logger,
  responseErrorToString,
} from '@zorro/common-utils';
import { YesNo } from '@zorro/types';
import {
  callEndpoint,
  showErrorNotification,
  useForm,
  useMonolithQuery,
} from '@zorro/web/ui-utils';

import {
  CompanySetupFormFields,
  getCompanySetupSchemaObject,
  getHrisProviderForUpdate,
} from './CompanySetupUtils';

export const useCompanySetupForm = (
  employerId: string | undefined,
  isFinalizationMode: boolean
) => {
  'use no memo';
  const queryClient = useQueryClient();

  const { data: employer } = useMonolithQuery({
    method: 'employersControllerFindOne',
    params: employerId && [employerId],
  });

  const { data: subEntities = [] } = useMonolithQuery({
    method: 'subEntityControllerFindMany',
    params: employerId && [employerId],
  });

  const { data: subEntities2 = [] } = useMonolithQuery({
    method: 'subEntity2ControllerFindMany',
    params: employerId && [employerId],
  });

  const companySetupForm = useForm<CompanySetupFormFields>({
    mode: 'all',
    resolver: yupResolver(getCompanySetupSchemaObject(isFinalizationMode)),
    defaultValues: {
      hrisProvider: employer?.hrisProvider ?? null,
      hrisProviderNotListedText: null,
      payrollCycle: employer?.payrollCycle,
      waitingPeriod: employer?.waitingPeriod,
      isApplicableLargeEmployer: isDefined(employer?.isApplicableLargeEmployer)
        ? convertToYesNoEnum(employer?.isApplicableLargeEmployer)
        : YesNo.NO,
      subEntities: subEntities.map((subEntity) => subEntity.name),
      subEntities2: subEntities2.map((subEntity) => subEntity.name),
    },
  });

  const isSubEntity2Enabled = subEntities2.length > 0;

  return {
    companySetupForm,
    isSubEntity2Enabled,
    onSubmit: async () => {
      const data = companySetupForm.getValues();

      try {
        if (employerId) {
          await callEndpoint({
            method: 'subEntityControllerUpsertMany',
            params: [employerId, data.subEntities],
          });

          await callEndpoint({
            method: 'subEntity2ControllerUpsertMany',
            params: [employerId, data.subEntities2],
          });

          await callEndpoint({
            method: 'employersControllerUpdate',
            params: [
              employerId,
              {
                hrisProvider: getHrisProviderForUpdate(
                  data.hrisProvider,
                  data.hrisProviderNotListedText
                ),
                payrollCycle: data.payrollCycle,
                waitingPeriod: data.waitingPeriod,
                isApplicableLargeEmployer: data.isApplicableLargeEmployer
                  ? convertFromYesNo(data.isApplicableLargeEmployer)
                  : null,
                enrollmentTeamIds: employer?.enrollmentTeamIds || [],
                zorroPartnerId: employer?.zorroPartnerId || null,
                producerId: employer?.producerId || null,
              },
            ],
          });

          setTimeout(async () => {
            await queryClient.invalidateQueries();
          }, 0);
          return true;
        }
      } catch (error) {
        logger.error(error);
        showErrorNotification({ message: responseErrorToString(error) });
        companySetupForm.reset();
      }

      return false;
    },
  };
};
